import { useEffect, useRef, useState } from 'react';
import { REACTION_EMOJI, REACTION_EMOJI_EXTRA, type ChatEvent, type ReactionEvent, type SuperReactionEvent } from '@srcbattle/shared';
import { api, ApiRequestError } from '../api.ts';
import { Avatar, Button } from './ui.tsx';

/**
 * Reacting during a clip.
 *
 * The point is to let the room respond without talking over the music — a
 * Discord call is one channel, and somebody shouting is the same as somebody
 * pausing. Eight emoji rather than a picker: a wall of choices takes longer to
 * use than saying the thing out loud, which defeats it.
 *
 * Each reaction floats for a few seconds with the sender's name and avatar and
 * then is gone. Nothing is stored anywhere — a reaction is a noise made in a
 * room, and a room does not keep a log of gasps.
 */

/** How long a card stays on screen. Long enough to read a name, short enough
 *  that five at once do not become a wall. */
const LIFETIME_MS = 3_000;

interface Floating extends ReactionEvent {
  /** Unique per card, since the same person can react twice in a second. */
  key: string;
  /** A little horizontal scatter so simultaneous reactions do not stack. */
  offset: number;
}

export function ReactionBar({ onSend }: { onSend: (emoji: string) => void }) {
  const [showMore, setShowMore] = useState(false);

  const button = (emoji: string) => (
    <button
      key={emoji}
      type="button"
      onClick={() => onSend(emoji)}
      aria-label={`React ${emoji}`}
      className="rounded-lg border border-border px-2.5 py-1 text-lg transition-colors hover:border-border-strong hover:bg-surface-raised active:scale-95"
    >
      {emoji}
    </button>
  );

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {REACTION_EMOJI.map(button)}
        <button
          type="button"
          onClick={() => setShowMore((open) => !open)}
          aria-expanded={showMore}
          aria-label="More reactions"
          className="rounded-lg border border-border px-2.5 py-1 text-sm text-text-faint transition-colors hover:border-border-strong hover:text-text"
        >
          {showMore ? '−' : '···'}
        </button>
      </div>
      {showMore && (
        <div className="flex flex-wrap gap-1.5 border-t border-border pt-1.5">
          {REACTION_EMOJI_EXTRA.map(button)}
        </div>
      )}
    </div>
  );
}

export function ChatComposer({ onSend }: { onSend: (text: string) => void }) {
  const [text, setText] = useState('');
  return (
    <form className="flex w-full gap-2" onSubmit={(event) => {
      event.preventDefault();
      const message = text.trim();
      if (!message) return;
      onSend(message);
      setText('');
    }}>
      <input aria-label="Chat message" value={text} maxLength={120}
        onChange={(event) => setText(event.target.value)} placeholder="Say something…"
        className="min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-accent" />
      <button type="submit" className="rounded-lg bg-accent px-3 py-2 text-sm text-accent-fg">Send</button>
    </form>
  );
}

export function SuperReactionComposer({ onSend }: { onSend: (imageUrl: string, text: string) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [text, setText] = useState('SUPER MESSAGE');
  const [error, setError] = useState<string | null>(null);
  const choose = async (file?: File) => {
    if (!file || !file.type.startsWith('image/')) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.uploadReaction(file);
      setPendingUrl(result.image.url);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That image did not upload.');
    } finally { setBusy(false); }
  };
  const discard = () => {
    setPendingUrl(null);
    setError(null);
  };
  if (pendingUrl) return <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-raised p-2">
    <img src={pendingUrl} alt="Super message preview" className="h-20 w-20 rounded-lg object-cover" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium">Ready to send?</p>
      <input
        aria-label="Super message text"
        value={text}
        maxLength={60}
        onChange={(event) => setText(event.target.value)}
        className="mt-2 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
      />
    </div>
    <Button variant="ghost" onClick={discard}>Change</Button>
    <Button onClick={() => { onSend(pendingUrl, text.trim() || 'SUPER MESSAGE'); setPendingUrl(null); }}>Send</Button>
  </div>;
  return <div tabIndex={0} onPaste={(event) => {
    const file = [...event.clipboardData.files].find((item) => item.type.startsWith('image/'));
    if (file) { event.preventDefault(); void choose(file); }
  }} className="rounded-lg border border-dashed border-border px-2 py-1 text-xs text-text-faint">
    <button type="button" disabled={busy} onClick={() => input.current?.click()} className="text-accent hover:underline">{busy ? 'Compressing…' : 'Super message'}</button>
    {' · paste or choose an image/GIF'}
    {error && <p className="mt-1 text-danger">{error}</p>}
    <input ref={input} hidden type="file" accept="image/*" onChange={(event) => { void choose(event.target.files?.[0]); event.target.value = ''; }} />
  </div>;
}

export function ReactionOverlay({ incoming }: { incoming: ReactionEvent | null }) {
  const [floating, setFloating] = useState<Floating[]>([]);

  /**
   * Every card's own removal timer, kept outside the effect.
   *
   * The obvious version returns `clearTimeout` from the effect, which reads as
   * tidy and is wrong: the effect re-runs on the *next* reaction, so cleaning
   * up cancels the previous card's removal and leaves it on screen for good. A
   * busy moment would paper the bottom of everyone's window permanently. These
   * are cleared on unmount instead, which is the only time they are stale.
   */
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (!incoming) return;

    const card: Floating = {
      ...incoming,
      key: `${incoming.userId}-${incoming.at}-${Math.random().toString(36).slice(2, 7)}`,
      offset: Math.round((Math.random() - 0.5) * 40),
    };
    setFloating((current) => [...current, card]);

    timers.current.push(
      setTimeout(() => {
        setFloating((current) => current.filter((f) => f.key !== card.key));
      }, LIFETIME_MS),
    );
  }, [incoming]);

  if (floating.length === 0) return null;

  return (
    /**
     * Fixed to the bottom-right and pointer-events-none.
     *
     * Deliberately not over the player: YouTube's terms forbid covering any
     * part of an embedded player, and a card that ate clicks would make the
     * operator's controls unreliable at the worst moment.
     */
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex flex-col items-center gap-1.5 px-4"
    >
      {floating.map((card) => (
        <div
          key={card.key}
          style={{ transform: `translateX(${card.offset}px)` }}
          className="flex items-center gap-2 rounded-full border border-border bg-surface-raised/95 px-3 py-1.5 shadow-lg backdrop-blur"
        >
          <Avatar value={card.avatarValue} size="sm" />
          <span className="text-sm">{card.displayName}</span>
          <span className="text-lg leading-none">{card.emoji}</span>
        </div>
      ))}
    </div>
  );
}

interface FloatingChat extends ChatEvent { key: string }

export function ChatOverlay({ incoming }: { incoming: ChatEvent | null }) {
  const [floating, setFloating] = useState<FloatingChat[]>([]);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => {
    if (!incoming) return;
    const card = { ...incoming, key: `${incoming.userId}-${incoming.at}-${Math.random().toString(36).slice(2, 7)}` };
    setFloating((current) => [...current.slice(-3), card]);
    timers.current.push(setTimeout(
      () => setFloating((current) => current.filter((item) => item.key !== card.key)),
      5_000,
    ));
  }, [incoming]);

  if (!floating.length) return null;
  return (
    <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-20 z-40 flex flex-col items-center gap-2 px-4">
      {floating.map((card) => (
        <div key={card.key} className="flex max-w-xl items-center gap-2 rounded-2xl border border-border bg-surface-raised/95 px-4 py-2 shadow-xl backdrop-blur">
          <Avatar value={card.avatarValue} size="sm" />
          <strong className="text-sm">{card.displayName}</strong>
          <span className="break-words text-sm">{card.text}</span>
        </div>
      ))}
    </div>
  );
}

export function SuperReactionOverlay({ incoming, durationMs }: { incoming: SuperReactionEvent | null; durationMs: number }) {
  if (!incoming) return null;
  return <div key={`${incoming.userId}-${incoming.at}`} style={{ animationDuration: `${durationMs}ms` }} className="super-reaction pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-8">
    <div className="max-w-2xl rounded-3xl border-4 border-accent bg-surface p-4 text-center shadow-2xl">
      <img src={incoming.imageUrl} alt="Super message" className="max-h-[65vh] w-full rounded-2xl object-contain" />
      <p className="mt-2 font-black">{incoming.displayName}: {incoming.text}</p>
    </div>
  </div>;
}
